import { parseSSELines } from '../dashboard/askAica/apiHelper';
import { ReasoningStep } from '../dashboard/askAica/actions';
import { CitationSource } from '../dashboard/askAica/citationTypes';
import { GateOutcome } from './types';
import { GLOBAL_CHAT_CONFIG, getUserConversationsService, getConversationHistoryService } from './service';
import { GLOBAL_HISTORY_PAGE_SIZE } from './constants';

interface ChatMessage {
    role: string;
    content: string;
}

/** A single parsed SSE frame from /athena/chat. `conversation_id` is a TOP-LEVEL
 * field on the chunk (see Athena's ChatStreamChunk DTO, app/dto/chat.py) — it is
 * NEVER nested inside `metadata`. `gate_outcome` (and other gate deltas) DO arrive
 * inside `metadata`, merged cumulatively across chunks — mirrored from how the
 * leads stream carries llm_used/timestamp (see
 * store/dashboard/askAica/apiHelper.ts::processStreamChunk). */
interface GlobalStreamChunk {
    delta?: { role: string; content: string };
    message?: { role: string; content: string };
    is_final?: boolean;
    conversation_id?: string;
    reasoning?: ReasoningStep[] | null;
    has_new_reasoning?: boolean;
    metadata?: {
        gate_outcome?: GateOutcome;
        /** Citation sources — present ONLY on the final chunk (is_final: true),
         * same contract as the leads stream (see dashboard/askAica/apiHelper.ts). */
        sources?: CitationSource[];
        [key: string]: any;
    };
}

export interface GlobalAskAicaResponse {
    content: string;
    conversationId?: string;
    gateOutcome?: GateOutcome;
    reasoning?: ReasoningStep[] | null;
    hasNewReasoning?: boolean;
    sources?: CitationSource[];
}

export interface GateReply {
    selected_org_id: string;
}

export type GateAction = { switch: string } | { clear: true };

export interface StreamGlobalChatParams {
    messages: ChatMessage[];
    sources: string[];
    userId: string;
    /** The resolved company org, from state — omitted entirely for a company-less turn. */
    orgId?: string | null;
    /** Held across turns once captured from the stream; omitted on the first gate-run turn. */
    conversationId?: string | null;
    /** Tapping a candidate on the picker. */
    gateReply?: GateReply;
    /** Chip switch/clear affordances. */
    gateAction?: GateAction;
}

interface GlobalStreamState {
    lastServerContent: string;
    metadata: Record<string, any> | undefined;
    conversationId: string | undefined;
    gateOutcome: GateOutcome | undefined;
    /** Left `undefined` (never defaulted to null/false) when a chunk carries no
     * reasoning field at all — keeps onChunk's call shape unchanged for streams
     * that never emit reasoning (back-compat with existing apiHelper tests). */
    reasoning: ReasoningStep[] | null | undefined;
    hasNewReasoning: boolean | undefined;
}

const processGlobalStreamChunk = (
    parsedData: GlobalStreamChunk,
    state: GlobalStreamState,
    onChunk: (
        content: string,
        gateOutcome: GateOutcome | undefined,
        conversationId: string | undefined,
        reasoning?: ReasoningStep[] | null,
        hasNewReasoning?: boolean,
        sources?: CitationSource[],
    ) => void,
) => {
    if (parsedData.message?.content) {
        state.lastServerContent = parsedData.message.content;
    }

    // Merge (not replace) — gate_outcome may arrive on a later chunk than
    // llm_used/timestamp, or vice versa.
    state.metadata = { ...state.metadata, ...parsedData.metadata };

    // conversation_id is a TOP-LEVEL chunk field (see GlobalStreamChunk) — read it
    // the FIRST time it appears; do not gate on chunk position (a thinking/searching
    // frame may precede it).
    if (!state.conversationId && parsedData.conversation_id) {
        state.conversationId = parsedData.conversation_id;
    }
    if (!state.gateOutcome && state.metadata?.gate_outcome) {
        state.gateOutcome = state.metadata.gate_outcome;
    }

    // Mirrors the leads stream (dashboard/askAica/apiHelper.ts::processStreamChunk):
    // reasoning/has_new_reasoning are per-chunk, sources arrive only on the final chunk.
    // Only touched when the chunk actually carries the field — see GlobalStreamState.
    if (parsedData.reasoning !== undefined) state.reasoning = parsedData.reasoning;
    if (parsedData.has_new_reasoning !== undefined) state.hasNewReasoning = parsedData.has_new_reasoning;

    // Only pass the extended (reasoning/hasNewReasoning/sources) arguments when
    // THIS frame actually carries one of them, using the frame's OWN values —
    // never the accumulated `state.*` ones. `setReasoning` (globalAskAica/reducer.ts)
    // APPENDS on every dispatch, so re-sending an already-dispatched reasoning
    // delta on every later frame (which is what reading off `state.reasoning`
    // did) duplicated the reasoning block. Keeps onChunk's call shape exactly
    // 3-arg for streams that never emit them (back-compat with existing callers/tests).
    const sources = parsedData.metadata?.sources;
    if (parsedData.reasoning !== undefined || parsedData.has_new_reasoning !== undefined || sources !== undefined) {
        onChunk(
            state.lastServerContent,
            state.gateOutcome,
            state.conversationId,
            parsedData.reasoning,
            parsedData.has_new_reasoning,
            sources,
        );
    } else {
        onChunk(state.lastServerContent, state.gateOutcome, state.conversationId);
    }
};

/**
 * Streams a chat turn to Athena via raw XMLHttpRequest.
 *
 * INVARIANT: this MUST stay XHR, never axios — the app-wide axios
 * instance carries a stale `x-organization-id` default header (see
 * utils/axios-interceptor.ts::addTokenToHeaders) that would silently defeat the
 * company-less/company-switch gate. XHR only ever sends the headers set below.
 */
export const streamGlobalAskAicaChat = (
    params: StreamGlobalChatParams,
    onChunk: (
        content: string,
        gateOutcome: GateOutcome | undefined,
        conversationId: string | undefined,
        reasoning?: ReasoningStep[] | null,
        hasNewReasoning?: boolean,
        sources?: CitationSource[],
    ) => void,
    onComplete: (response: GlobalAskAicaResponse) => void,
    onError: (error: any) => void,
): (() => void) => {
    const xhr = new XMLHttpRequest();
    // Set by the returned abort function — guards every callback below so a stream
    // that's been superseded (new turn started, or chat reset) can never dispatch
    // a late chunk/completion, even if the browser fires one more readystatechange
    // event as part of tearing the XHR down (see RECUR-27369).
    let aborted = false;

    const state: GlobalStreamState = {
        lastServerContent: '',
        metadata: undefined,
        conversationId: params.conversationId ?? undefined,
        gateOutcome: undefined,
        reasoning: undefined,
        hasNewReasoning: undefined,
    };
    let processedLength = 0;
    let lineBuffer = '';

    const handleParsedChunk = (parsedData: GlobalStreamChunk) => processGlobalStreamChunk(parsedData, state, onChunk);

    xhr.open('POST', GLOBAL_CHAT_CONFIG.URL);

    const headers = GLOBAL_CHAT_CONFIG.getHeaders(params.orgId ?? null, params.userId);
    Object.keys(headers).forEach((key) => {
        xhr.setRequestHeader(key, headers[key]);
    });

    xhr.onreadystatechange = function () {
        if (aborted) return;
        if (xhr.readyState === 3 || xhr.readyState === 4) {
            const newData = xhr.responseText.substring(processedLength);
            processedLength = xhr.responseText.length;

            if (newData) {
                const combined = lineBuffer + newData;
                const lines = combined.split('\n');
                lineBuffer = lines.pop() || '';
                parseSSELines(lines, handleParsedChunk);
            }
        }

        if (xhr.readyState === 4) {
            if (lineBuffer.trim()) {
                parseSSELines([lineBuffer], handleParsedChunk);
                lineBuffer = '';
            }

            if (xhr.status >= 200 && xhr.status < 300) {
                onComplete({
                    content: state.lastServerContent,
                    conversationId: state.conversationId,
                    gateOutcome: state.gateOutcome,
                    reasoning: state.reasoning,
                    hasNewReasoning: state.hasNewReasoning,
                    sources: state.metadata?.sources,
                });
            } else {
                const error = new Error(`HTTP error! Status: ${xhr.status}`);
                // Carried for the caller's diagnostic log only (see runTurn's onError in
                // useGlobalAskAica.ts) — xhr.status/responseText aren't in scope there.
                (error as any).status = xhr.status;
                (error as any).responseSnippet = xhr.responseText?.slice(0, 500);
                onError(error);
            }
        }
    };

    xhr.onerror = function () {
        if (aborted) return;
        const error = new Error('Network error occurred');
        (error as any).status = xhr.status;
        (error as any).responseSnippet = xhr.responseText?.slice(0, 500);
        onError(error);
    };

    const body: Record<string, any> = {
        user_id: params.userId,
        sources: params.sources,
        messages: params.messages,
        stream: true,
        source: 'portfolio',
    };
    if (params.conversationId) body.conversation_id = params.conversationId;
    // Company-less invariant: org_id is only ever added when explicitly resolved —
    // never falls back to a stored/default value.
    if (params.orgId) body.org_id = params.orgId;
    if (params.gateReply) body.gate_reply = params.gateReply;
    if (params.gateAction) body.gate_action = params.gateAction;

    xhr.send(JSON.stringify(body));

    return () => {
        aborted = true;
        xhr.abort();
    };
};

/**
 * Fetch the logged-in user's conversation list (GET /chat/conversations/user).
 * 401 (no platformUserId principal) is treated as an empty-list state, not a
 * crash — the sidebar renders empty/error rather than taking down the chat.
 */
export const getUserConversations = async (
    userId: string | undefined,
    handlers: { onSuccess: (data: any) => void; onError?: (err: any) => void },
) => {
    try {
        const response = await getUserConversationsService(userId);
        handlers.onSuccess(response.data);
    } catch (err) {
        // Never log the raw error — an AxiosError's `config.headers` carries the
        // session cerberus token (see useAskAicaFileActions.ts::openFile).
        console.error('Error fetching user conversations:', err instanceof Error ? err.message : 'unknown error');
        handlers.onError?.(err);
    }
};

export const getConversationHistory = async (conversationId: string, userId?: string, orgId?: string | null) => {
    // F-9: explicit page_size so a full history restore isn't silently truncated
    // to the shared endpoint's default.
    const response = await getConversationHistoryService(conversationId, userId, orgId, GLOBAL_HISTORY_PAGE_SIZE);
    return response.data;
};
